<template>
  <div class="pa-4 pa-md-6">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="rgba(255, 255, 255, 0.9)"
      color="#1A237E"
      spinner="mini-spinner"
    />

    <v-row class="mb-6 align-center">
      <v-col cols="12" sm="6">
        <h2 class="page-title">Sighting Reports</h2>
        <p class="caption grey--text font-weight-bold text-uppercase mb-0">
          Total Records in Database: {{ totalRecords }}
        </p>
      </v-col>
      
      <v-col cols="12" sm="3">
        <v-select
          v-model="spotType"
          :items="spotTypes"
          label="Filter Habitat"
          outlined
          dense
          hide-details
          clearable
          color="primary"
          class="rounded-lg"
        ></v-select>
      </v-col>

      <v-col cols="12" sm="3" class="text-right">
        <download-excel 
          :fetch="fetchAllReports" 
          :fields="json_fields" 
          :name="'WhaleShark_Full_Export_' + new Date().toISOString().slice(0,10) + '.xls'"
          :before-generate="() => appLoading = true"
          :before-finish="() => appLoading = false"
        >
          <v-btn block depressed color="#1A237E" dark class="rounded-lg font-weight-bold">
            <v-icon left>mdi-file-excel</v-icon>
            Download All (Excel)
          </v-btn>
        </download-excel>
      </v-col>
    </v-row>

    <v-card flat class="rounded-xl border-light elevation-1 overflow-hidden">
      <v-simple-table class="modern-table">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-overline">No.</th>
              <th class="text-overline">Habitat</th>
              <th class="text-overline">Date</th>
              <th class="text-overline">Reporter</th>
              <th class="text-overline">Coordinates</th>
              <th class="text-overline">Length (M)</th>
              <th class="text-overline">Vessel Detail</th>
              <th class="text-overline">Health</th>
              <th class="text-overline text-center">View</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(value, i) in report" :key="i" class="row-hover">
              <td class="font-weight-bold grey--text">{{ (page - 1) * 20 + (i + 1) }}</td>
              <td>
                <v-chip x-small :color="value.field === 'In Net' ? 'error' : 'info'" dark class="font-weight-black">
                  {{ value.field || 'N/A' }}
                </v-chip>
              </td>
              <td class="caption font-weight-bold">{{ value.create_date ? value.create_date.slice(0, 10) : 'N/A' }}</td>
              <td>
                <div class="font-weight-bold text-no-wrap">
                  {{ value.userid ? value.userid.first_name + ' ' + (value.userid.last_name || '') : 'Unknown' }}
                </div>
                <div class="caption grey--text">{{ value.userid ? value.userid.phone : '-' }}</div>
              </td>
              <td class="caption">
                <div v-if="value.location && value.location.length >= 2">
                   <div class="primary--text font-weight-bold">{{ value.location[1].toFixed(4) }} N</div>
                   <div class="blue--text font-weight-bold">{{ value.location[0].toFixed(4) }} E</div>
                </div>
                <div v-else class="grey--text">No GPS</div>
              </td>
              <td><v-chip small outlined color="primary" class="font-weight-black">{{ value.length || '0' }} M</v-chip></td>
              <td>
                <div class="font-weight-bold">{{ value.boatname || '-' }}</div>
                <div class="caption grey--text">{{ value.boatno || '-' }}</div>
              </td>
              <td>
                <v-icon small :color="value.health ? 'success' : 'grey lighten-2'">
                  {{ value.health ? 'mdi-heart-pulse' : 'mdi-help-circle-outline' }}
                </v-icon>
                <span class="caption ml-1">{{ value.health || 'Unset' }}</span>
              </td>
              <td class="text-center">
                <v-btn icon color="primary" :to="'/admin/uploaddetail?id=' + value._id">
                  <v-icon>mdi-eye-circle</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>

    <div class="text-center pt-6">
      <v-pagination v-model="page" :length="Pagelength" :total-visible="7" circle color="#1A237E"></v-pagination>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import JsonExcel from "vue-json-excel";

export default {
  components: {
    'download-excel': JsonExcel
  },
  data() {
    return {
      appLoading: false,
      report: [],
      page: 1,
      Pagelength: 0,
      totalRecords: 0,
      spotTypes: ["In Net", "In Natural Water"],
      spotType: null,

      json_fields: {
        "Habitat": "spottype",
        "Date": "date",
        "Reporter": "username",
        "Phone": "phone",
        "Latitude": "lat",
        "Longitude": "lon",
        "Gender": "gender",
        "Length (M)": "length",
        "Boat Name": "bname",
        "Boat No": "bno",
        "Owner": "owner",
        "Condition": "health",
        "Description": "info"
      },
    };
  },

  mounted() {
    this.getData();
  },

  watch: {
    page() { this.getData(); },
    spotType() { this.page = 1; this.getData(); }
  },

  methods: {
    // Fetches paged data for the table UI
    getData() {
      this.appLoading = true;
      axios({
        url: "/admin/view/all/reports",
        method: "get",
        headers: { token: localStorage.getItem("token") },
        params: {
          page: this.page,
          limit: 20,
          field: this.spotType,
        },
      })
      .then((response) => {
        this.appLoading = false;
        if (response.data.data) {
          this.report = response.data.data;
          this.totalRecords = response.data.totalLength;
          this.Pagelength = Math.ceil(response.data.totalLength / 20);
        }
      })
      .catch(() => { this.appLoading = false; });
    },

    // ASYNC FETCH: This gets EVERY record from the DB for Excel
    async fetchAllReports() {
      try {
        const response = await axios({
          url: "/admin/view/all/reports",
          method: "get",
          headers: { token: localStorage.getItem("token") },
          params: {
            // By NOT sending 'page' or 'limit', or sending a massive limit, 
            // your backend should return all records.
            limit: 5000, 
            field: this.spotType,
          },
        });

        // Format the full list for Excel
        return response.data.data.map((item) => ({
          spottype: item.field || 'N/A',
          date: item.create_date ? item.create_date.slice(0, 10) : 'N/A',
          username: item.userid ? (item.userid.first_name + ' ' + (item.userid.last_name || '')) : 'Unknown',
          phone: item.userid ? item.userid.phone : 'N/A',
          lat: (item.location && item.location[1]) ? item.location[1] : '-',
          lon: (item.location && item.location[0]) ? item.location[0] : '-',
          gender: item.gender || 'N/A',
          length: item.length || '0',
          bname: item.boatname || 'N/A',
          bno: item.boatno || 'N/A',
          owner: item.ownerName || 'N/A',
          health: item.health || 'N/A',
          info: item.description || 'N/A'
        }));
      } catch (err) {
        console.error("Excel download error", err);
        return [];
      }
    }
  },
};
</script>

<style scoped>
.page-title { font-family: 'Syne', sans-serif; font-weight: 800; color: #1A237E; text-transform: uppercase; }
.modern-table thead th { background-color: #F8FAFC !important; color: #546E7A !important; font-weight: 800 !important; }
.row-hover:hover { background-color: #F1F4F9 !important; transition: 0.2s; }
.border-light { border: 1px solid rgba(0,0,0,0.08) !important; }
.text-overline { font-size: 0.7rem !important; font-weight: 900 !important; }
</style>